'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, scaleIn, viewportOnce, spring, springSoft, staggerContainer, fadeUp } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Workflow,
  Zap,
  Link2,
  Database,
  Filter,
  Route,
  Sparkles,
  ShieldCheck,
  Code2,
  FileCheck,
  Headset,
  Check,
  ArrowRight,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Tints — brand sky-blue (#0098FF) family only.                             */
/* ────────────────────────────────────────────────────────────────────────── */

const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Content                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

const HERO = {
  eyebrow: 'Platform Capability',
  title: 'CX Software Integrations',
  tagline: 'Unify Your CX Ecosystem as a Single, Smart Unit',
  body: [
    'Bring your tools, data, and teams into one unified interface — so every insight leads to action, and every action drives impact.',
    'From marketing to CX to operations, plug into a connected engine that simplifies complexity and sharpens decisions. Locobuzz connects directly with leading CRM systems, BI tools, and enterprise helpdesks.',
  ],
};

/* DRAFT — review */
const IMPACT = [
  { value: '1,000+', label: 'Apps and connectors available' },
  { value: '99.9%', label: 'Uptime SLA across all integrations' },
  { value: '<5 min', label: 'Median time to first sync' },
  { value: '2-way', label: 'Bi-directional real-time data sync' },
];

const CAPABILITIES = [
  { Icon: Workflow, title: 'Simplify Setup, Maximize Motion', text: 'Step-by-step guides make integration feel like intuition.', tint: 'blue' },
  { Icon: Zap,      title: 'Trigger Actions Automatically',   text: 'Define triggers and flows that keep your workflows moving.', tint: 'violet' },
  { Icon: Link2,    title: 'Connect Key Systems Instantly',   text: 'Effortlessly integrate Locobuzz with the apps you already trust.', tint: 'sky' },
  { Icon: Database, title: 'Unify Data, Uncover Direction',   text: 'Bring CRMs, CDPs and your teams together for one simple view of truth.', tint: 'blue' },
  { Icon: Filter,   title: 'Custom Push',                     text: 'Curate what gets synced. Handpick tickets, tailor fields, full control over CRM data flow.', tint: 'violet' },
  { Icon: Route,    title: 'Automatic Routing',               text: 'Set it and scale it. Every ticket is instantly pushed to your CRM — nothing falls through.', tint: 'sky' },
];

/* Integration chips for the rotating orbit — official vendor logos from
   /public/integrations. White chip + contained logo (matches the homepage
   integration cloud). 12 logos spread across 3 rings (3 / 4 / 5). */

const ORBIT_LOGOS = {
  ring1: [
    { name: 'Salesforce',         src: '/integrations/salesforce-1.png' },
    { name: 'Zendesk',            src: '/integrations/zendesk-2.png' },
    { name: 'Slack',              src: '/integrations/slack.png' },
  ],
  ring2: [
    { name: 'Microsoft Dynamics', src: '/integrations/microsoft-dynamics.png' },
    { name: 'Freshdesk',          src: '/integrations/freskdesk.png' },
    { name: 'Zoho',               src: '/integrations/zoho-1.png' },
    { name: 'Asana',              src: '/integrations/asana.png' },
  ],
  ring3: [
    { name: 'Jira',               src: '/integrations/jira.png' },
    { name: 'Trello',             src: '/integrations/trello.png' },
    { name: 'Airtable',           src: '/integrations/airtable.png' },
    { name: 'ClickUp',            src: '/integrations/clickup.png' },
    { name: 'Google Sheets',      src: '/integrations/google-sheet.png' },
  ],
};

const INTEGRATION_TYPES = [
  {
    Icon: Zap,
    badge: 'Plug-and-Play',
    title: 'Prebuilt Integrations',
    desc: 'Seamless connections with leading platforms via the Locobuzz Integration Hub. For brands seeking fast, no-code integrations with common business tools.',
    bestFor: ['Fast time-to-value', 'No-code setup', 'Common CRMs & helpdesks'],
    tint: 'blue',
    body: 'orbit',
  },
  {
    Icon: Code2,
    badge: 'Bespoke',
    title: 'Custom Integrations',
    desc: 'For enterprises needing bespoke connections to niche, legacy, or proprietary systems.',
    useCases: ['Legacy CRMs', 'Proprietary Tools', 'On-prem Databases', 'Internal APIs', 'Industry-led ERPs'],
    bestFor: ['Highly regulated industries', 'Complex workflows', 'Enterprise compliance'],
    tint: 'violet',
    body: 'useCases',
  },
  {
    Icon: Workflow,
    badge: 'Embedded',
    title: 'Native CRM Integration',
    desc: 'For teams that want to work inside their CRM with full Locobuzz power built in. Embedded with Salesforce & MS Dynamics.',
    embedded: ['Salesforce', 'Microsoft Dynamics'],
    bestFor: ['Agent-centric workflows', 'CRM-led CX', 'No context switching'],
    tint: 'sky',
    body: 'embedded',
  },
];

const TRUST_PILLARS = [
  { Icon: ShieldCheck, title: 'Role-Based Access',      desc: 'Granular permissions and full audit trails on every action.' },
  { Icon: Code2,       title: 'Flexible API Framework', desc: 'REST APIs, webhooks, and SDKs for custom data pipelines.' },
  { Icon: FileCheck,   title: 'Compliance-Ready',       desc: 'GDPR, ISO 27001, and SOC 2 aligned by design.' },
  { Icon: Headset,     title: '24/7 Enterprise Support', desc: 'High-availability infrastructure with round-the-clock coverage.' },
];

/* DRAFT — review */
const FAQS = [
  {
    q: 'Which platforms does Locobuzz integrate with out of the box?',
    a: 'Locobuzz ships with prebuilt connectors for Salesforce, Zendesk, HubSpot, Microsoft Dynamics, Freshdesk, Zoho CRM, Slack, Jira, Asana, Power BI, and 1,000+ more through the Integration Hub — with zero coding required.',
  },
  {
    q: 'How long does it take to go live with an integration?',
    a: 'Most prebuilt integrations are live in under five minutes. Our setup wizard walks through authentication, field mapping, and a test sync. Custom or embedded integrations typically go live within a guided onboarding sprint.',
  },
  {
    q: 'Is the data sync real-time or batch-based?',
    a: 'All standard integrations use bi-directional real-time sync — mentions, tickets, and CRM records move the moment they change, not on a schedule. Batch options are available for high-volume data-warehouse pipelines.',
  },
  {
    q: 'Can we control which data fields get pushed to our CRM?',
    a: 'Yes. The Custom Push feature lets you handpick exactly which fields — ticket type, sentiment score, channel, tag — flow into your CRM. Field-level audit trails log every sync action for compliance.',
  },
  {
    q: 'Does Locobuzz support enterprise compliance requirements?',
    a: 'Locobuzz is GDPR, ISO 27001, and SOC 2 aligned by design. Role-based access controls, full audit trails, and high-availability infrastructure make it suitable for regulated industries and global enterprise deployments.',
  },
];

/* DRAFT — review */
const PANEL = {
  title: 'Connected Stack',
  badge: '1,000+ integrations',
  kpiValue: 48320,
  kpiLabel: 'Syncs today',
  trend: [12, 18, 15, 24, 30, 27, 38, 35, 46, 54],
  trendDelta: '+38%',
  segments: [
    { label: 'Active',  pct: 72, tone: 'pos' },
    { label: 'Queued',  pct: 20, tone: 'neu' },
    { label: 'Error',   pct: 8,  tone: 'neg' },
  ],
  feedLabel: 'Recent sync events',
  feed: [
    { text: 'Ticket #4821 synced to Salesforce Case', meta: 'CRM sync · 2s ago', tag: 'Salesforce', tone: 'pos' },
    { text: 'Mention escalated to Zendesk ticket',    meta: 'Auto-route · 5s ago', tag: 'Zendesk', tone: 'pos' },
    { text: 'Slack alert queued — rate limit reached', meta: 'Channel alert · 12s ago', tag: 'Slack', tone: 'neu' },
    { text: 'NPS score pushed to HubSpot contact',    meta: 'CRM sync · 18s ago', tag: 'HubSpot', tone: 'pos' },
    { text: 'Webhook retry pending — endpoint timeout', meta: 'Webhook · 31s ago', tag: 'API', tone: 'neg' },
  ],
  chip: 'Bi-directional · real-time sync',
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Reusable bits                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : size === 'md' ? 48 : 44;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div
      style={{
        width: dim, height: dim, borderRadius: 14,
        background: T.bg,
        border: `1px solid ${T.stroke}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

/* ── Integration orbit ─────────────────────────────────────────────────── */

function LogoChip({ logo, size = 44 }) {
  return (
    <div
      title={logo.name}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: '#fff',
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 4px 14px rgba(0,152,255,0.10), 0 1px 2px rgba(0,0,0,0.06)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <img
        src={logo.src}
        alt={logo.name}
        style={{ width: '62%', height: '62%', objectFit: 'contain', display: 'block' }}
      />
    </div>
  );
}

function IntegrationOrbit() {
  const rings = [
    { id: 1, radius: 80,  size: 38, duration: 35, logos: ORBIT_LOGOS.ring1, reverse: false },
    { id: 2, radius: 130, size: 42, duration: 50, logos: ORBIT_LOGOS.ring2, reverse: true  },
    { id: 3, radius: 180, size: 38, duration: 70, logos: ORBIT_LOGOS.ring3, reverse: false },
  ];

  return (
    <div
      className="lli-orbit"
      style={{ position: 'relative', width: '100%', maxWidth: 440, aspectRatio: '1 / 1', margin: '0 auto' }}
    >
      {/* ring guides */}
      <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} aria-hidden="true">
        {rings.map((r) => (
          <circle
            key={r.id}
            cx="200" cy="200" r={r.radius}
            fill="none"
            stroke="#0098FF"
            strokeOpacity="0.20"
            strokeDasharray="2 4"
          />
        ))}
      </svg>

      {/* center logo — Locobuzz favicon */}
      <img
        src="/integrations/locobuzz-favicon.webp"
        alt="Locobuzz"
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 84, height: 84,
          borderRadius: 22, display: 'block',
          boxShadow: '0 8px 28px rgba(0,152,255,0.45), 0 2px 6px rgba(0,0,0,0.12)',
          zIndex: 3,
        }}
      />

      {/* orbiting rings */}
      {rings.map((r) => (
        <div
          key={r.id}
          className={`lli-orbit-ring ${r.reverse ? 'lli-orbit-ring--reverse' : ''}`}
          style={{ position: 'absolute', inset: 0, animationDuration: `${r.duration}s` }}
        >
          {r.logos.map((logo, i) => {
            const angle = (i / r.logos.length) * 2 * Math.PI - Math.PI / 2;
            const x = Math.cos(angle) * r.radius;
            const y = Math.sin(angle) * r.radius;
            return (
              <div
                key={logo.name}
                className="lli-orbit-chip"
                style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  animationDuration: `${r.duration}s`,
                  animationDirection: r.reverse ? 'normal' : 'reverse',
                }}
              >
                <LogoChip logo={logo} size={r.size} />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Page                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */

export default function IntegrationsPage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="lli-hero-orbs">
          <span className="lli-orb lli-orb1" />
          <span className="lli-orb lli-orb2" />
        </div>
        <InlineStyle css={`
          .lli-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .lli-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .lli-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:lliOrb1 22s ease-in-out infinite; }
          .lli-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:lliOrb2 26s ease-in-out infinite; }
          @keyframes lliOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes lliOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .lli-orb{animation:none;} }

          @keyframes lli-orbit-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
          @keyframes lli-orbit-spin-reverse {
            from { transform: rotate(0deg); }
            to   { transform: rotate(-360deg); }
          }
          .lli-orbit-ring {
            animation-name: lli-orbit-spin;
            animation-iteration-count: infinite;
            animation-timing-function: linear;
          }
          .lli-orbit-ring--reverse { animation-name: lli-orbit-spin-reverse; }
          .lli-orbit:hover .lli-orbit-ring { animation-play-state: paused; }
          .lli-orbit-chip:hover { z-index: 5; }
          @media (prefers-reduced-motion: reduce) { .lli-orbit-ring { animation: none !important; } }

          @media (max-width: 1024px) {
            .lli-integration-types { grid-template-columns: 1fr !important; }
            .lli-trust-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          }
          @media (max-width: 520px) {
            .lli-trust-grid { grid-template-columns: 1fr !important; }
          }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Integrations')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Integrations')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('CX Software')} <span className="serif-accent italic">{t('Integrations')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── CAPABILITIES ──────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('Connect the Dots')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>
              {t('Connect the Dots. Command the')} <span className="serif-accent italic">{t('Outcome.')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 740, margin: '0 auto', lineHeight: 1.7 }}>
              {t('Eliminate data silos, automate cross-platform workflows, and ensure your CX and marketing teams operate from a single source of truth — from CRMs and care platforms to social networks and analytics tools.')}
            </p>
          </motion.div>

          <motion.div
            className="lb-grid-3"
            variants={staggerContainer()}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {CAPABILITIES.map((p) => (
              <motion.div
                key={p.title}
                className="lb-card"
                variants={fadeUp}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>
                  {t(p.title)}
                </h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── INTEGRATION TYPES ─────────────────────────────────────────── */}
      <section className="lb-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('Flexible by Design')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>
              {t('Three Ways to Integrate.')} <span className="serif-accent italic">{t('One Connected System.')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 700, margin: '0 auto', lineHeight: 1.7 }}>
              {t('Choose how integrations work best for your team — manual for precision, automatic for scale, embedded for in-CRM teams.')}
            </p>
          </motion.div>

          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 24 }}
            className="lli-integration-types"
            variants={staggerContainer(0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {INTEGRATION_TYPES.map((type) => {
              const T = TINTS[type.tint];
              return (
                <motion.div
                  key={type.title}
                  className="lb-card"
                  variants={fadeUp}
                  style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
                >
                  {/* header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <IconTile Icon={type.Icon} tint={type.tint} size="md" />
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: T.accent, letterSpacing: '1.4px', textTransform: 'uppercase', marginBottom: 4 }}>
                        {t(type.badge)}
                      </div>
                      <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 18, color: 'var(--text-heading)', lineHeight: 1.25, margin: 0 }}>
                        {t(type.title)}
                      </h3>
                    </div>
                  </div>

                  <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65, margin: 0 }}>{t(type.desc)}</p>

                  {type.body === 'orbit' && (
                    <div style={{ background: T.bg, border: `1px solid ${T.stroke}`, borderRadius: 'var(--radius-xl)', padding: 14, position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', maxHeight: 180, margin: '0 auto' }}>
                        <svg viewBox="0 0 200 200" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
                          {[40, 65, 88].map((r) => (
                            <circle key={r} cx="100" cy="100" r={r} fill="none" stroke={T.accent} strokeOpacity="0.22" strokeDasharray="2 3" />
                          ))}
                        </svg>
                        <img
                          src="/integrations/locobuzz-favicon.webp"
                          alt="Locobuzz"
                          loading="lazy"
                          decoding="async"
                          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 38, height: 38, borderRadius: 10, display: 'block', boxShadow: `0 4px 14px rgba(0,152,255,0.4)` }}
                        />
                        {[
                          { angle:   0, r: 65, logo: ORBIT_LOGOS.ring1[0] },
                          { angle:  60, r: 88, logo: ORBIT_LOGOS.ring2[0] },
                          { angle: 120, r: 65, logo: ORBIT_LOGOS.ring1[2] },
                          { angle: 180, r: 88, logo: ORBIT_LOGOS.ring2[2] },
                          { angle: 240, r: 65, logo: ORBIT_LOGOS.ring1[1] },
                          { angle: 300, r: 88, logo: ORBIT_LOGOS.ring2[3] },
                        ].map((c, i) => {
                          const rad = (c.angle * Math.PI) / 180;
                          return (
                            <div
                              key={i}
                              style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(calc(-50% + ${Math.cos(rad) * c.r}px), calc(-50% + ${Math.sin(rad) * c.r}px))` }}
                            >
                              <LogoChip logo={c.logo} size={26} />
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, fontWeight: 700, color: T.accent, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                        {t('CRM & Workflow Tools')}
                      </div>
                    </div>
                  )}

                  {type.body === 'useCases' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', letterSpacing: '1.4px', textTransform: 'uppercase' }}>{t('Use Cases')}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {type.useCases.map((u) => (
                          <span key={u} className="lb-pill" style={{ background: T.bg, color: T.accent, border: `1px solid ${T.stroke}`, fontSize: 12 }}>
                            {t(u)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {type.body === 'embedded' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', letterSpacing: '1.4px', textTransform: 'uppercase' }}>{t('Embedded In')}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {type.embedded.map((platform) => (
                          <div key={platform} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: `1px solid ${T.stroke}`, borderRadius: 'var(--radius-lg)' }}>
                            <LogoChip
                              logo={
                                platform === 'Salesforce'
                                  ? { name: platform, src: '/integrations/salesforce-1.png' }
                                  : { name: platform, src: '/integrations/microsoft-dynamics.png' }
                              }
                              size={28}
                            />
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-heading)', lineHeight: 1.2 }}>{platform}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t('Native embedded')}</div>
                            </div>
                            <ArrowRight size={14} color={T.accent} style={{ marginLeft: 'auto' }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ height: 1, background: 'var(--border-light)', margin: '4px 0' }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', letterSpacing: '1.4px', textTransform: 'uppercase' }}>{t('Best For')}</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {type.bestFor.map((b, j) => (
                        <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <span
                            aria-hidden="true"
                            style={{ flexShrink: 0, width: 16, height: 16, borderRadius: '50%', background: T.bg, border: `1.5px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}
                          >
                            <Check size={8} color={T.accent} strokeWidth={3} />
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{t(b)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── BIG ORBIT VISUAL ─────────────────────────────────────────────── */}
      <section className="lb-section-alt" style={{ padding: '64px 0' }}>
        <div className="container">
          <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
            <motion.div variants={slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand)', letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>
                {t('Integration Hub')}
              </div>
              <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: 'var(--text-heading)', lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>
                {t('1000+ CRMs and Applications in One')} <span className="serif-accent italic">{t('Connected Hub')}</span>
              </h3>
              <p style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: '0 0 24px' }}>
                {t('Locobuzz sits at the center of your tech stack — pulling in mentions, conversations, tickets, and signals, and pushing them out as clean, actionable events into your CRM, BI, helpdesk, and chat tools.')}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  'Two-way sync — push & pull data in real time',
                  'Field-level mapping for total control of what’s shared',
                  'Trigger-based automations across systems',
                  'Field-level audit trails on every sync action',
                ].map((b, j) => (
                  <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span
                      aria-hidden="true"
                      style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-subtle)', border: '1.5px solid #BFE3FF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}
                    >
                      <Check size={11} color="var(--brand)" strokeWidth={3} />
                    </span>
                    <span style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.65 }}>{t(b)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce}>
              <div style={{ borderRadius: 'var(--radius-3xl)', padding: 32, background: 'linear-gradient(135deg, #ffffff 0%, #EAF6FF 100%)', border: '1px solid #BFE3FF', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
                <IntegrationOrbit />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TRUST PILLARS ────────────────────────────────────────────── */}
      <section className="lb-section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ShieldCheck size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('Enterprise-Ready')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>
              {t('Secure, Scalable &')} <span className="serif-accent italic">{t('Ready for Enterprise')}</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
              {t('Built to meet the bar of regulated industries, large CX organizations, and global brands.')}
            </p>
          </motion.div>

          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}
            className="lli-trust-grid"
            variants={staggerContainer(0.08)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {TRUST_PILLARS.map((pillar) => (
              <motion.div
                key={pillar.title}
                className="lb-card"
                variants={fadeUp}
                style={{ textAlign: 'center', padding: '28px 18px' }}
              >
                <div style={{ display: 'inline-flex', marginBottom: 14 }}>
                  <IconTile Icon={pillar.Icon} tint="blue" size="md" />
                </div>
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: 'var(--text-heading)', lineHeight: 1.3, margin: '0 0 8px' }}>
                  {t(pillar.title)}
                </h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55, margin: 0 }}>{t(pillar.desc)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {/* DRAFT — review */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="integrations-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
